/**
 * BatchesToPayTab Component
 * Displays batches awaiting payment with selection and payment totals
 * Mobile cards / Desktop table with checkbox selection
 */

import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  CreditCardIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { OathButton, OathEmptyState, OathAlert, OathSpinner } from '../../../shared/OathUI';
import BatchCard from './BatchCard';
import PaymentPlanCard from './PaymentPlanCard';
import PlanDueHint from './PlanDueHint';
import RequestAchInfoPrompt from './RequestAchInfoPrompt';
import {
  formatCurrency,
  formatCurrencyWithSymbol,
  getBatchId,
  getBatchTypeBadge,
  isLinkableBatch,
  isPaymentPlanBatch,
  getPlanAmountDue,
  describeBatch,
  roundCurrency,
  clampCurrency,
  formatAmountOnBlur,
  nonNegativeAmount,
  BATCH_TYPE_LABELS,
  BATCH_TYPE_BADGES
} from '../utils/formatters';
import { useIsMobile, useClickVsDrag } from '../utils/hooks';
import usePermissions from '../../../../hooks/usePermissions';

// Chip color variants — keyed by the `color` field on each BATCH_TYPE_BADGES entry.
// Tailwind JIT can't generate classes from template literals, so the variants are written out.
const CHIP_VARIANTS = {
  blue: {
    inactive: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-blue-900/40',
    active:   'bg-blue-200 text-blue-800 border-blue-400 dark:bg-blue-900/60 dark:text-blue-200 dark:border-blue-400',
    countInactive: 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200',
    countActive:   'bg-blue-700 text-white dark:bg-blue-300 dark:text-blue-900',
  },
  purple: {
    inactive: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 hover:border-purple-300 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800 dark:hover:bg-purple-900/40',
    active:   'bg-purple-200 text-purple-800 border-purple-400 dark:bg-purple-900/60 dark:text-purple-200 dark:border-purple-400',
    countInactive: 'bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-200',
    countActive:   'bg-purple-700 text-white dark:bg-purple-300 dark:text-purple-900',
  },
  amber: {
    inactive: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 hover:border-amber-300 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800 dark:hover:bg-amber-900/40',
    active:   'bg-amber-200 text-amber-800 border-amber-400 dark:bg-amber-900/60 dark:text-amber-200 dark:border-amber-400',
    countInactive: 'bg-amber-100 text-amber-700 dark:bg-amber-800 dark:text-amber-200',
    countActive:   'bg-amber-700 text-white dark:bg-amber-300 dark:text-amber-900',
  },
  rose: {
    inactive: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 hover:border-rose-300 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800 dark:hover:bg-rose-900/40',
    active:   'bg-rose-200 text-rose-800 border-rose-400 dark:bg-rose-900/60 dark:text-rose-200 dark:border-rose-400',
    countInactive: 'bg-rose-100 text-rose-700 dark:bg-rose-800 dark:text-rose-200',
    countActive:   'bg-rose-700 text-white dark:bg-rose-300 dark:text-rose-900',
  },
  emerald: {
    inactive: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900/40',
    active:   'bg-emerald-200 text-emerald-800 border-emerald-400 dark:bg-emerald-900/60 dark:text-emerald-200 dark:border-emerald-400',
    countInactive: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-800 dark:text-emerald-200',
    countActive:   'bg-emerald-700 text-white dark:bg-emerald-300 dark:text-emerald-900',
  },
  teal: {
    inactive: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100 hover:border-teal-300 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-800 dark:hover:bg-teal-900/40',
    active:   'bg-teal-200 text-teal-800 border-teal-400 dark:bg-teal-900/60 dark:text-teal-200 dark:border-teal-400',
    countInactive: 'bg-teal-100 text-teal-700 dark:bg-teal-800 dark:text-teal-200',
    countActive:   'bg-teal-700 text-white dark:bg-teal-300 dark:text-teal-900',
  },
};

// Active plans with no PaymentPlan batch row to expand from. Only BUF plans get a collection
// batch automatically on activation; a BSS plan waits until staff create one, and an AR plan
// collects through its own linked batches, which may not be open. Without this section those
// plans came back from the API and then silently vanished (Bug 4395). They are informational
// only: nothing here is selectable or counted in the totals.
const PaymentPlansSection = ({ plans, allowVoid = true, onVoided }) => (
  <div className="mt-6">
    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
      Payment plans
    </h3>
    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
      These balances have not been added to a batch yet, so they can't be paid online until Bankers Surety adds them.
    </p>
    <div className="space-y-3">
      {plans.map((plan) => (
        <PaymentPlanCard key={plan.id} plan={plan} allowVoid={allowVoid} onVoided={onVoided} />
      ))}
    </div>
  </div>
);

const BatchesToPayTab = ({
  batches = [],
  onPaymentSubmit,
  isLoading = false,
  agencies = [],
  selectedFilterAgencyId,
  onAgencyFilterChange,
  paymentPlans = [],
  onPaymentPlanVoided
}) => {
  const { canAccessElectronicPayment } = usePermissions();
  const isMobile = useIsMobile();
  const { handleMouseDown, isClick } = useClickVsDrag();
  const [selectedBatches, setSelectedBatches] = useState(new Set());
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [pendingAgencyChange, setPendingAgencyChange] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(new Set());
  // Which plan batch has its detail panel open (payment history + void). Keyed by batch
  // id, not plan id — an AR plan can have several linked batch rows, and each row's
  // panel should open independently.
  const [expandedBatchId, setExpandedBatchId] = useState(null);

  // Payment plans arrive as REAL batches now: a BUF/BSS plan gets a collection batch
  // holding its balance as a BRC, and an AR plan's own linked batches are relabelled.
  // They are selected, amount-edited, totalled and paid exactly like any other batch —
  // paying one feeds the plan server-side. Nothing here is synthetic, so none of the
  // old selection guards remain.
  const planDetailsById = useMemo(() => {
    const map = new Map();
    (paymentPlans || []).forEach((plan) => map.set(plan.id, plan));
    return map;
  }, [paymentPlans]);

  // Plans that no batch row will expand into. Checked against the unfiltered list so a plan
  // with a row keeps exactly one card (its expander) whatever filters are applied. Paid-off
  // plans are left out: nothing is owed on them.
  const unbatchedPlans = useMemo(() => {
    const batchedPlanIds = new Set(
      batches.filter(isPaymentPlanBatch).map((b) => b.paymentPlanId)
    );
    return (paymentPlans || []).filter((plan) =>
      !batchedPlanIds.has(plan.id)
      && !plan.isPaidOff
      && (Number(plan.remainingBalance) || 0) > 0
    );
  }, [batches, paymentPlans]);

  // Which known batch-type chips should appear (only types actually present in the data).
  const availableTypes = useMemo(() => {
    const present = new Set(batches.map(b => b?.batchType).filter(Boolean));
    return Object.keys(BATCH_TYPE_LABELS).filter(t => present.has(t));
  }, [batches]);

  const toggleTypeFilter = useCallback((type) => {
    setTypeFilter(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  // Filter batches by selected agency, then by type chips, then by search query.
  // Search matches batch #, defendant name, and associated report # case-insensitively.
  const filteredBatches = useMemo(() => {
    let result = selectedFilterAgencyId
      ? batches.filter(batch => batch.agencyId === selectedFilterAgencyId)
      : batches;

    if (typeFilter.size > 0) {
      result = result.filter(b => typeFilter.has(b?.batchType));
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(batch => {
        const batchId = String(getBatchId(batch) ?? '').toLowerCase();
        const reportNumber = String(batch.reportNumber ?? '').toLowerCase();
        const defendant = String(batch.defendantName ?? '').toLowerCase();
        const associated = String(batch.associatedReportNumber ?? '').toLowerCase();
        // Plan batches are searchable by plan name and debt source ("BUF", "AR", "BSS").
        const planName = String(batch.paymentPlanName ?? '').toLowerCase();
        const planType = String(batch.paymentPlanTypeName ?? '').toLowerCase();
        return (
          batchId.includes(q) ||
          reportNumber.includes(q) ||
          defendant.includes(q) ||
          associated.includes(q) ||
          planName.includes(q) ||
          planType.includes(q)
        );
      });
    }

    return result;
  }, [batches, selectedFilterAgencyId, typeFilter, searchQuery]);

  const hasBatchRows = filteredBatches.length > 0;

  // Handle agency filter change with confirmation if items selected
  const handleAgencyFilterChange = useCallback((newAgencyId) => {
    if (selectedBatches.size > 0 && newAgencyId !== selectedFilterAgencyId) {
      setPendingAgencyChange(newAgencyId);
    } else {
      onAgencyFilterChange(newAgencyId);
    }
  }, [selectedBatches.size, selectedFilterAgencyId, onAgencyFilterChange]);

  // Confirm agency change and clear selections
  const confirmAgencyChange = useCallback(() => {
    if (pendingAgencyChange !== null) {
      setSelectedBatches(new Set());
      onAgencyFilterChange(pendingAgencyChange);
      setPendingAgencyChange(null);
    }
  }, [pendingAgencyChange, onAgencyFilterChange]);

  // Cancel agency change
  const cancelAgencyChange = useCallback(() => {
    setPendingAgencyChange(null);
  }, []);

  const handleRowClick = (e, batch, isSelected) => {
    // Only toggle if it was a click, not a drag
    if (isClick(e)) {
      handleSelectBatch(batch, !isSelected);
    }
  };

  const getBatchInquiryUrl = useCallback((batch) => {
    const batchId = getBatchId(batch);
    return `/app/power-inquiry?agencyIds=${batch.agencyId}&viewMode=byReport&batchId=${batchId}`;
  }, []);

  // The expandable plan detail is only offered when the plans endpoint actually returned
  // that plan — READ:AR gates it, so a user can see a plan batch without its detail.
  const getExpandablePlan = useCallback(
    (batch) => (isPaymentPlanBatch(batch) ? planDetailsById.get(batch.paymentPlanId) : undefined),
    [planDetailsById]
  );

  // Takes the batch (not an id) so it can be handed to every BatchCard as one stable
  // callback without breaking their memo.
  const toggleBatchExpanded = useCallback((batch) => {
    const batchId = getBatchId(batch);
    setExpandedBatchId((prev) => (prev === batchId ? null : batchId));
  }, []);

  // Initialize payment amounts when batches change. Store as 2-decimal strings so
  // the inputs render "17.50" instead of "17.5" the moment a batch is selected.
  // Credit buckets (negative due) seed as "0.00" — the visible Due column still shows
  // the real credit, but the payable amount is $0.
  //
  // The plans request runs in parallel with the batches request and usually lands later
  // (and again after every void/refresh). When only the plans changed, nothing the agent
  // has typed is touched: just the BUF default of a plan batch is swapped for the
  // due-now amount, and only while it still holds the untouched full-balance default.
  const seededBatchesRef = useRef(null);
  useEffect(() => {
    const batchesChanged = seededBatchesRef.current !== batches;
    seededBatchesRef.current = batches;

    setPaymentAmounts((prev) => {
      const next = batchesChanged ? {} : { ...prev };
      batches.forEach((batch) => {
        const batchId = getBatchId(batch);
        const bufKey = `${batchId}-openBuf`;
        // API returns totalNet/totalBuf/totalMisc
        const bufDefault = formatAmountOnBlur(nonNegativeAmount(batch.totalBuf ?? batch.openBuf));
        // A plan's collection batch carries the whole plan balance; default to what is due
        // this month instead so the plan isn't paid off by accident (the agent can still
        // raise it up to the full balance to pay ahead).
        const planDue = getPlanAmountDue(batch, planDetailsById.get(batch.paymentPlanId));

        if (batchesChanged) {
          next[`${batchId}-openNet`] = formatAmountOnBlur(nonNegativeAmount(batch.totalNet ?? batch.openNet));
          next[bufKey] = planDue != null ? formatAmountOnBlur(planDue) : bufDefault;
          next[`${batchId}-openMisc`] = formatAmountOnBlur(nonNegativeAmount(batch.totalMisc ?? batch.openMisc));
        } else if (planDue != null && next[bufKey] === bufDefault) {
          next[bufKey] = formatAmountOnBlur(planDue);
        }
      });
      return next;
    });

    // A batch paid elsewhere drops out of the refreshed list; drop it from the selection
    // too so it can't ride along into the next payment (Bug 4430).
    if (batchesChanged) {
      const listedIds = new Set(batches.map((batch) => getBatchId(batch)));
      setSelectedBatches((prev) => {
        const kept = [...prev].filter((batchId) => listedIds.has(batchId));
        return kept.length === prev.size ? prev : new Set(kept);
      });
    }
  }, [batches, planDetailsById]);

  // Handle batch selection
  const handleSelectBatch = useCallback((batch, selected) => {
    const batchId = getBatchId(batch);
    setSelectedBatches((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(batchId);
      } else {
        newSet.delete(batchId);
      }
      return newSet;
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedBatches(new Set(filteredBatches.map((b) => getBatchId(b))));
    } else {
      setSelectedBatches(new Set());
    }
  }, [filteredBatches]);

  // Handle amount change
  const handleAmountChange = useCallback((batchId, field, value) => {
    setPaymentAmounts((prev) => ({
      ...prev,
      [`${batchId}-${field}`]: value
    }));
  }, []);

  // Calculate totals for selected batches (memoized for performance)
  // Uses roundCurrency to avoid floating point precision issues
  const totals = useMemo(() => {
    let totalNet = 0;
    let totalBuf = 0;
    let totalMisc = 0;

    selectedBatches.forEach((batchId) => {
      const netVal = paymentAmounts[`${batchId}-openNet`];
      const bufVal = paymentAmounts[`${batchId}-openBuf`];
      const miscVal = paymentAmounts[`${batchId}-openMisc`];
      totalNet += roundCurrency(nonNegativeAmount(netVal));
      totalBuf += roundCurrency(nonNegativeAmount(bufVal));
      totalMisc += roundCurrency(nonNegativeAmount(miscVal));
    });

    return {
      totalNet: roundCurrency(totalNet),
      totalBuf: roundCurrency(totalBuf),
      totalMisc: roundCurrency(totalMisc),
      grandTotal: roundCurrency(totalNet + totalBuf + totalMisc)
    };
  }, [selectedBatches, paymentAmounts]);

  // Row subtotals across everything visible (memoized for performance — this avoids
  // recalculating on every render when typing in inputs). Plan batches are included:
  // their BRC is real batch money in the BUF bucket, so leaving it out would understate
  // what the agency owes.
  const footerTotals = useMemo(() => {
    let totalNet = 0;
    let totalBuf = 0;
    let totalMisc = 0;

    filteredBatches.forEach((batch) => {
      // Credits (negative buckets) are visible on the row but contribute $0 to the
      // payable footer total — matches what the user can actually pay.
      totalNet += nonNegativeAmount(batch.totalNet ?? batch.openNet);
      totalBuf += nonNegativeAmount(batch.totalBuf ?? batch.openBuf);
      totalMisc += nonNegativeAmount(batch.totalMisc ?? batch.openMisc);
    });

    return {
      totalNet: roundCurrency(totalNet),
      totalBuf: roundCurrency(totalBuf),
      totalMisc: roundCurrency(totalMisc),
      grandTotal: roundCurrency(totalNet + totalBuf + totalMisc)
    };
  }, [filteredBatches]);

  const allSelected = filteredBatches.length > 0 && selectedBatches.size === filteredBatches.length;
  const someSelected = selectedBatches.size > 0;

  // Handle payment submit
  const handlePaymentClick = () => {
    if (onPaymentSubmit && selectedBatches.size > 0) {
      onPaymentSubmit(selectedBatches, paymentAmounts);
    }
  };

  // Show loading state. The plans request is deliberately not awaited here: it only
  // feeds the expand panels (which appear once plan data exists), so a zero-batch
  // agency shows "Nothing to pay" immediately instead of spinning on it.
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <OathSpinner size="lg" />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading outstanding batches...</p>
      </div>
    );
  }

  if (batches.length === 0) {
    const emptyState = (
      <OathEmptyState
        icon={CheckIcon}
        title="Nothing to pay"
        message="Good news! There are no outstanding batches to pay."
      />
    );
    if (unbatchedPlans.length === 0) return emptyState;
    return (
      <div>
        {emptyState}
        <PaymentPlansSection
          plans={unbatchedPlans}
          allowVoid={canAccessElectronicPayment}
          onVoided={onPaymentPlanVoided}
        />
      </div>
    );
  }

  // Read-only view for users without electronic payment permission
  if (!canAccessElectronicPayment) {
    return (
      <div>
        {/* ACH Promo Disclaimer */}
        <RequestAchInfoPrompt agencyId={selectedFilterAgencyId || agencies[0]?.agencyId} layout="promo" />

        {/* Agency Filter Dropdown - read-only view */}
        {agencies.length > 1 && (
          <div className="mb-4 sm:mb-5 sm:p-4 sm:bg-gray-50 sm:dark:bg-gray-800/50 sm:rounded-xl sm:border sm:border-gray-200 sm:dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <BuildingOfficeIcon className="h-5 w-5" />
                <span className="text-sm font-medium">Agency</span>
              </div>
              <div className="relative flex-1 sm:max-w-md">
                <BuildingOfficeIcon className="sm:hidden absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <select
                  value={selectedFilterAgencyId || 'all'}
                  onChange={(e) => onAgencyFilterChange(e.target.value === 'all' ? null : parseInt(e.target.value))}
                  className="w-full pl-9 sm:pl-4 pr-10 py-2.5 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors appearance-none cursor-pointer"
                >
                  <option value="all">All Agencies ({batches.length})</option>
                  {agencies.map((agency) => (
                    <option key={agency.agencyId} value={agency.agencyId}>
                      {agency.agencyId} - {agency.name}{agency.isMainAgency ? ' (Main)' : ''} ({agency.unpaidBatchCount})
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
              </div>
            </div>
          </div>
        )}

        {/* Quick search */}
        <div className="mb-3">
          <div className="relative max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by batch # or description..."
              className="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Type filter chips — colored to match the inline badges in the data set */}
        {availableTypes.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4 sm:mb-5">
            {availableTypes.map(type => {
              const isActive = typeFilter.has(type);
              const variant = CHIP_VARIANTS[BATCH_TYPE_BADGES[type]?.color] || CHIP_VARIANTS.blue;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleTypeFilter(type)}
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                    isActive ? variant.active : variant.inactive
                  }`}
                >
                  {BATCH_TYPE_LABELS[type]}
                </button>
              );
            })}
            {typeFilter.size > 0 && (
              <button
                type="button"
                onClick={() => setTypeFilter(new Set())}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Read-only table */}
        {isMobile ? (
          <div className="space-y-3">
            {filteredBatches.map((batch) => {
              const batchId = getBatchId(batch);
              const expandablePlan = getExpandablePlan(batch);
              const isExpanded = !!expandablePlan && expandedBatchId === batchId;
              return (
                <Fragment key={batchId}>
                  <BatchCard
                    batch={batch}
                    selected={false}
                    onSelect={() => {}}
                    paymentAmounts={paymentAmounts}
                    onAmountChange={() => {}}
                    getBatchInquiryUrl={getBatchInquiryUrl}
                    disabled
                    expanded={isExpanded}
                    onToggleExpand={expandablePlan ? toggleBatchExpanded : undefined}
                  />
                  {isExpanded && (
                    <div className="mb-3 -mt-2">
                      {/* No electronic-payment permission in this branch, so no Void. */}
                      <PaymentPlanCard plan={expandablePlan} allowVoid={false} />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              {hasBatchRows && (
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Batch #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      NET Due
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      MISC Due
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      BUF Due
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Total Owed
                    </th>
                  </tr>
                </thead>
              )}
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                {filteredBatches.map((batch) => {
                  const batchId = getBatchId(batch);
                  const netDue = batch.totalNet || batch.openNet || 0;
                  const bufDue = batch.totalBuf || batch.openBuf || 0;
                  const miscDue = batch.totalMisc || batch.openMisc || 0;
                  const totalOwed = nonNegativeAmount(netDue) + nonNegativeAmount(bufDue) + nonNegativeAmount(miscDue);
                  const typeBadge = getBatchTypeBadge(batch);
                  const expandablePlan = getExpandablePlan(batch);
                  const isExpanded = !!expandablePlan && expandedBatchId === batchId;
                  return (
                    <Fragment key={batchId}>
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <div className="flex items-center gap-2">
                            {expandablePlan && (
                              <button
                                type="button"
                                onClick={() => toggleBatchExpanded(batch)}
                                aria-label={isExpanded ? 'Hide plan details' : 'Show plan details'}
                                aria-expanded={isExpanded}
                                className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200"
                              >
                                {isExpanded
                                  ? <ChevronDownIcon className="h-4 w-4" />
                                  : <ChevronRightIcon className="h-4 w-4" />}
                              </button>
                            )}
                            {!isLinkableBatch(batch) ? (
                              <span>{batch.reportNumber || batchId}</span>
                            ) : (
                              <a
                                href={getBatchInquiryUrl(batch)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary dark:text-primary-light hover:underline cursor-pointer font-medium text-sm"
                              >
                                {batch.reportNumber || batchId}
                              </a>
                            )}
                            {typeBadge && (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${typeBadge.bgColor} ${typeBadge.textColor}`}>
                                {typeBadge.label}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {describeBatch(batch)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                          {formatCurrencyWithSymbol(netDue)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                          {formatCurrencyWithSymbol(miscDue)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                          {formatCurrencyWithSymbol(bufDue)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-primary dark:text-primary-light text-right">
                          {formatCurrencyWithSymbol(totalOwed)}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-teal-50/40 dark:bg-teal-900/10">
                          <td colSpan={6} className="px-4 py-4">
                            {/* No electronic-payment permission in this branch, so no Void. */}
                            <PaymentPlanCard plan={expandablePlan} allowVoid={false} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>

              {hasBatchRows && (
                <tfoot className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-600">
                  <tr>
                    <td colSpan={2} className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">
                      Total due
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-right">
                      {formatCurrencyWithSymbol(footerTotals.totalNet)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-right">
                      {formatCurrencyWithSymbol(footerTotals.totalMisc)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-right">
                      {formatCurrencyWithSymbol(footerTotals.totalBuf)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-primary dark:text-primary-light text-right">
                      {formatCurrencyWithSymbol(footerTotals.grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {unbatchedPlans.length > 0 && (
          // No electronic-payment permission in this branch, so no Void.
          <PaymentPlansSection plans={unbatchedPlans} allowVoid={false} />
        )}

      </div>
    );
  }

  return (
    <div>
      {/* Agency Filter Dropdown - only show when multiple agencies have batches */}
      {agencies.length > 1 && (
        <div className="mb-4 sm:mb-5 sm:p-4 sm:bg-gray-50 sm:dark:bg-gray-800/50 sm:rounded-xl sm:border sm:border-gray-200 sm:dark:border-gray-700">
          <div className="flex items-center gap-3">
            {/* Label - hidden on mobile, shown on desktop */}
            <div className="hidden sm:flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <BuildingOfficeIcon className="h-5 w-5" />
              <span className="text-sm font-medium">Agency</span>
            </div>
            {/* Select with icon inside on mobile */}
            <div className="relative flex-1 sm:max-w-md">
              <BuildingOfficeIcon className="sm:hidden absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <select
                value={selectedFilterAgencyId || 'all'}
                onChange={(e) => handleAgencyFilterChange(e.target.value === 'all' ? null : parseInt(e.target.value))}
                className="w-full pl-9 sm:pl-4 pr-10 py-2.5 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors appearance-none cursor-pointer"
              >
                <option value="all">All Agencies ({batches.length})</option>
                {agencies.map((agency) => (
                  <option key={agency.agencyId} value={agency.agencyId}>
                    {agency.agencyId} - {agency.name}{agency.isMainAgency ? ' (Main)' : ''} ({agency.unpaidBatchCount})
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>
      )}

      {/* Quick search */}
      <div className="mb-3">
        <div className="relative max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by batch # or description..."
            className="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Clear search"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Type filter chips — colored to match the inline badges in the data set */}
      {availableTypes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 sm:mb-5">
          {availableTypes.map(type => {
            const isActive = typeFilter.has(type);
            const variant = CHIP_VARIANTS[BATCH_TYPE_BADGES[type]?.color] || CHIP_VARIANTS.blue;
            return (
              <button
                key={type}
                type="button"
                onClick={() => toggleTypeFilter(type)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  isActive ? variant.active : variant.inactive
                }`}
              >
                {BATCH_TYPE_LABELS[type]}
              </button>
            );
          })}
          {typeFilter.size > 0 && (
            <button
              type="button"
              onClick={() => setTypeFilter(new Set())}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Agency Change Confirmation Dialog */}
      {pendingAgencyChange !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 sm:p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Clear Selections?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              You have {selectedBatches.size} batch(es) selected. Switching agencies will clear your current selections.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelAgencyChange}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAgencyChange}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selection Summary */}
      {someSelected && (
        <OathAlert variant="primary" className="mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <span className="font-medium">{selectedBatches.size} batch(es) selected</span>
              <span className="text-sm ml-2">
                Total: <strong>${formatCurrency(totals.grandTotal)}</strong>
              </span>
            </div>
            <OathButton onClick={handlePaymentClick} icon={CreditCardIcon}>
              Continue to Payment
            </OathButton>
          </div>
        </OathAlert>
      )}

      {/* Totals Summary (visible when batches selected) */}
      {someSelected && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Payment Summary
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total NET</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                ${formatCurrency(totals.totalNet)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total BUF</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                ${formatCurrency(totals.totalBuf)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total MISC</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                ${formatCurrency(totals.totalMisc)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Grand Total</p>
              <p className="text-lg font-bold text-primary dark:text-primary-light">
                ${formatCurrency(totals.grandTotal)}
              </p>
            </div>
          </div>
        </div>
      )}

      {isMobile ? (
        /* Mobile Card View */
        <div>
          {/* Select All */}
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Select All ({filteredBatches.length})
            </span>
          </div>

          {/* Batch Cards */}
          <div className="space-y-3">
            {filteredBatches.map((batch) => {
              const batchId = getBatchId(batch);
              const expandablePlan = getExpandablePlan(batch);
              const isExpanded = !!expandablePlan && expandedBatchId === batchId;
              return (
                <Fragment key={batchId}>
                  <BatchCard
                    batch={batch}
                    selected={selectedBatches.has(batchId)}
                    onSelect={handleSelectBatch}
                    paymentAmounts={paymentAmounts}
                    onAmountChange={handleAmountChange}
                    getBatchInquiryUrl={getBatchInquiryUrl}
                    expanded={isExpanded}
                    onToggleExpand={expandablePlan ? toggleBatchExpanded : undefined}
                    planAmountDue={getPlanAmountDue(batch, expandablePlan)}
                  />
                  {isExpanded && (
                    <div className="mb-3 -mt-2">
                      {/* Detail only — paying happens through the batch checkout. */}
                      <PaymentPlanCard plan={expandablePlan} onVoided={onPaymentPlanVoided} />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      ) : (
        /* Desktop Table View */
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            {hasBatchRows && (
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Batch #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    NET Due
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    MISC Due
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    BUF Due
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Total Owed
                  </th>
                </tr>
              </thead>
            )}
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {filteredBatches.map((batch) => {
                const batchId = getBatchId(batch);
                const isSelected = selectedBatches.has(batchId);
                // API returns totalNet/totalBuf/totalMisc
                const netDue = batch.totalNet || batch.openNet || 0;
                const bufDue = batch.totalBuf || batch.openBuf || 0;
                const miscDue = batch.totalMisc || batch.openMisc || 0;
                // Clamped to match the read-only table and the payable footer total: a
                // credit bucket contributes $0, it never offsets what is owed elsewhere.
                const totalOwed = nonNegativeAmount(netDue) + nonNegativeAmount(bufDue) + nonNegativeAmount(miscDue);
                const typeBadge = getBatchTypeBadge(batch);
                const expandablePlan = getExpandablePlan(batch);
                const isExpanded = !!expandablePlan && expandedBatchId === batchId;

                return (
                  <Fragment key={batchId}>
                  <tr
                    className={`transition-colors cursor-pointer ${
                      isSelected ? 'bg-primary-50/50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    onMouseDown={handleMouseDown}
                    onClick={(e) => handleRowClick(e, batch, isSelected)}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleSelectBatch(batch, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary accent-primary focus:ring-primary dark:bg-gray-700"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      <div className="flex items-center gap-2">
                        {expandablePlan && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleBatchExpanded(batch); }}
                            aria-label={isExpanded ? 'Hide plan details' : 'Show plan details'}
                            aria-expanded={isExpanded}
                            className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200"
                          >
                            {isExpanded
                              ? <ChevronDownIcon className="h-4 w-4" />
                              : <ChevronRightIcon className="h-4 w-4" />}
                          </button>
                        )}
                        {!isLinkableBatch(batch) ? (
                          <span>{batch.reportNumber || batchId}</span>
                        ) : (
                          <a
                            href={getBatchInquiryUrl(batch)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-primary dark:text-primary-light hover:underline cursor-pointer font-medium text-sm"
                          >
                            {batch.reportNumber || batchId}
                          </a>
                        )}
                        {typeBadge && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${typeBadge.bgColor} ${typeBadge.textColor}`}>
                            {typeBadge.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {describeBatch(batch)}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {isSelected ? (
                        <input
                          type="number"
                          value={paymentAmounts[`${batchId}-openNet`] ?? formatAmountOnBlur(netDue)}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              handleAmountChange(batchId, 'openNet', '');
                            } else {
                              const num = parseFloat(val);
                              handleAmountChange(batchId, 'openNet', isNaN(num) ? '' : clampCurrency(num, netDue));
                            }
                          }}
                          onBlur={(e) => handleAmountChange(batchId, 'openNet', formatAmountOnBlur(e.target.value))}
                          disabled={netDue <= 0}
                          className="w-20 sm:w-28 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-primary text-right disabled:opacity-50 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          min="0"
                          max={netDue}
                          step="0.01"
                        />
                      ) : (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {formatCurrencyWithSymbol(netDue)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {isSelected ? (
                        <input
                          type="number"
                          value={paymentAmounts[`${batchId}-openMisc`] ?? formatAmountOnBlur(miscDue)}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              handleAmountChange(batchId, 'openMisc', '');
                            } else {
                              const num = parseFloat(val);
                              handleAmountChange(batchId, 'openMisc', isNaN(num) ? '' : clampCurrency(num, miscDue));
                            }
                          }}
                          onBlur={(e) => handleAmountChange(batchId, 'openMisc', formatAmountOnBlur(e.target.value))}
                          disabled={miscDue <= 0}
                          className="w-20 sm:w-28 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-primary text-right disabled:opacity-50 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          min="0"
                          max={miscDue}
                          step="0.01"
                        />
                      ) : (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {formatCurrencyWithSymbol(miscDue)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {isSelected ? (
                        <input
                          type="number"
                          value={paymentAmounts[`${batchId}-openBuf`] ?? formatAmountOnBlur(bufDue)}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              handleAmountChange(batchId, 'openBuf', '');
                            } else {
                              const num = parseFloat(val);
                              handleAmountChange(batchId, 'openBuf', isNaN(num) ? '' : clampCurrency(num, bufDue));
                            }
                          }}
                          onBlur={(e) => handleAmountChange(batchId, 'openBuf', formatAmountOnBlur(e.target.value))}
                          disabled={bufDue <= 0}
                          className="w-20 sm:w-28 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-primary text-right disabled:opacity-50 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          min="0"
                          max={bufDue}
                          step="0.01"
                        />
                      ) : (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {formatCurrencyWithSymbol(bufDue)}
                        </span>
                      )}
                      <PlanDueHint amountDue={getPlanAmountDue(batch, planDetailsById.get(batch.paymentPlanId))} balance={bufDue} />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-primary dark:text-primary-light text-right">
                      {formatCurrencyWithSymbol(totalOwed)}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-teal-50/40 dark:bg-teal-900/10">
                      <td colSpan={7} className="px-4 py-4">
                        {/* Detail only — paying happens through the batch checkout above. */}
                        <PaymentPlanCard
                          plan={expandablePlan}
                          onVoided={onPaymentPlanVoided}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>

            {/* Totals Rows - uses memoized footerTotals for performance */}
            {hasBatchRows && (
              <tfoot className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-600">
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">
                    Total due
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-right">
                    {formatCurrencyWithSymbol(footerTotals.totalNet)}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-right">
                    {formatCurrencyWithSymbol(footerTotals.totalMisc)}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-right">
                    {formatCurrencyWithSymbol(footerTotals.totalBuf)}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-primary dark:text-primary-light text-right">
                    {formatCurrencyWithSymbol(footerTotals.grandTotal)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {unbatchedPlans.length > 0 && (
        <PaymentPlansSection plans={unbatchedPlans} onVoided={onPaymentPlanVoided} />
      )}

      {/* Bottom Action Bar (visible when batches selected) */}
      {someSelected && !isMobile && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <OathButton onClick={handlePaymentClick} icon={CreditCardIcon} size="lg">
            Pay ${formatCurrency(totals.grandTotal)} for {selectedBatches.size} Batch(es)
          </OathButton>
        </div>
      )}

    </div>
  );
};

export default BatchesToPayTab;
